import * as React from "react";
import { Dialog as SheetPrimitive } from "radix-ui";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * A panel over the page, anchored to an edge.
 *
 * Three sides. Right is a record over the list that found it; left is the
 * navigation sidebar on a phone; bottom is the mobile filter sheet (#349) —
 * the one that reads as a drawer pulled up from the device's own edge rather
 * than a desktop panel that happened to get narrow, which is what a left or
 * right sheet would have looked like at phone width.
 *
 * Built on Radix's Dialog, so the focus trap, the Escape handler, the scroll
 * lock and the return of focus to whatever opened it are the primitive's job
 * rather than ours.
 */
function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/40",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        "motion-reduce:animate-none",
        className,
      )}
      {...props}
    />
  );
}

function SheetTitle({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return <SheetPrimitive.Title data-slot="sheet-title" {...props} />;
}

function SheetDescription({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description data-slot="sheet-description" {...props} />
  );
}

/**
 * The overlay, the edge it slides from, and nothing else.
 *
 * Its caller owns the header, which is why this sits apart from
 * `SheetContent`: the sidebar's mobile panel already carries one, and a second
 * title row with a second close button above it would be chrome describing
 * chrome. A `SheetTitle` somewhere inside is not optional — Radix names the
 * dialog from it and warns when there is none.
 */
function SheetPanel({
  className,
  side = "right",
  children,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "left" | "right" | "bottom";
}) {
  return (
    <SheetPrimitive.Portal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "bg-background fixed z-50 flex flex-col shadow-lg",
          side === "right" &&
            "inset-y-0 right-0 border-l data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
          side === "left" &&
            "inset-y-0 left-0 border-r data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
          // Near-full height rather than a fixed fraction: a dozen fields plus
          // an open drill-in's option list is routinely taller than half a
          // phone screen, and a sheet that clipped its own content would just
          // move the scrolling problem rather than solve it.
          side === "bottom" &&
            "inset-x-0 bottom-0 h-[94vh] rounded-t-xl border-t data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "duration-200 motion-reduce:animate-none",
          className,
        )}
        {...props}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

/**
 * A panel with the standard header: the way out, the name, and any actions.
 *
 * `title` is required rather than optional: Radix warns without a
 * `Dialog.Title`, and more to the point a panel that covers the page owes the
 * reader a name — and a screen reader the same name.
 */
function SheetContent({
  className,
  children,
  title,
  description,
  actions,
  ...props
}: React.ComponentProps<typeof SheetPanel> & {
  title: string;
  description?: string;
  /** Rendered in the header, right side. */
  actions?: React.ReactNode;
}) {
  return (
    <SheetPanel className={className} {...props}>
      {/*
        Close on the left, per the convention this follows: the panel opens
        from the right, so its leading edge is where the eye lands and where
        the way out belongs. It is also the first thing in the tab order,
        which is the same argument made for the keyboard.
      */}
      <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 sm:px-6">
        <SheetPrimitive.Close
          className={cn(
            "text-muted-foreground hover:bg-accent hover:text-foreground",
            "focus-visible:ring-ring -ml-1 rounded-md p-1.5 transition-colors",
            "focus-visible:ring-2 focus-visible:outline-none",
          )}
        >
          <X className="size-4" aria-hidden />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>

        <div className="min-w-0 flex-1">
          <SheetTitle className="truncate text-base font-semibold tracking-tight">
            {title}
          </SheetTitle>
          {description ? (
            <SheetDescription className="text-muted-foreground truncate text-sm">
              {description}
            </SheetDescription>
          ) : (
            // Radix warns when a Dialog has no description. Absent one, say
            // so explicitly rather than passing an empty string.
            <SheetDescription className="sr-only">{title}</SheetDescription>
          )}
        </div>

        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </SheetPanel>
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetOverlay,
  SheetPanel,
  SheetTitle,
};
